import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Package, Shield, Plus, ArrowLeft, LogOut, ClipboardList, Eye, EyeOff, Bell, AlertTriangle, Image as ImageIcon, Upload, Trash2, Globe, FileText, Download, Edit, BookOpen, Mail, Printer, X } from 'lucide-react';
import DealersTab from './admin/DealersTab';
import KitOrdersTab from './admin/KitOrdersTab';
import GalleryTab from './admin/GalleryTab';
import BlogTab from './admin/BlogTab';
import DIYOrdersTab from './admin/DIYOrdersTab';
import MarketingTab from './admin/MarketingTab';
import ContactRequestsTab from './admin/ContactRequestsTab';
import DemoPromoteTab from './admin/DemoPromoteTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [kitOrders, setKitOrders] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const getAuth = useCallback(() => btoa(`${credentials.username}:${credentials.password}`), [credentials]);

  const loadDashboardData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/dashboard`, { headers: { 'Authorization': `Basic ${getAuth()}` } });
      if (res.ok) setStats(await res.json());
      else if (res.status === 401) { setIsAuthenticated(false); setLoginError('Feil brukernavn eller passord'); }
    } catch { /* ignore */ }
  }, [getAuth]);

  const loadDealers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/dealers`, { headers: { 'Authorization': `Basic ${getAuth()}` } });
      if (res.ok) setDealers(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [getAuth]);

  const loadKitOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/kit-orders`, { headers: { 'Authorization': `Basic ${getAuth()}` } });
      if (res.ok) setKitOrders(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [getAuth]);

  const loadWarranties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/warranty`, { headers: { 'Authorization': `Basic ${getAuth()}` } });
      if (res.ok) setWarranties(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [getAuth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/admin/dashboard`, { headers: { 'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}` } });
      if (res.ok) {
        setIsAuthenticated(true);
        setStats(await res.json());
      } else {
        setLoginError('Feil brukernavn eller passord');
      }
    } catch {
      setLoginError('Tilkobling feilet');
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'dashboard') loadDashboardData();
    else if (activeTab === 'dealers') loadDealers();
    else if (activeTab === 'demo-promote') loadDealers();
    else if (activeTab === 'kits') loadKitOrders();
    else if (activeTab === 'warranties') loadWarranties();
  }, [activeTab, isAuthenticated, loadDashboardData, loadDealers, loadKitOrders, loadWarranties]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-stone-900 mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Dashboard</h1>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Brukernavn</label>
                <input type="text" value={credentials.username} onChange={(e) => setCredentials({...credentials, username: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" required data-testid="admin-username-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Passord</label>
                <input type="password" value={credentials.password} onChange={(e) => setCredentials({...credentials, password: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" required data-testid="admin-password-input" />
              </div>
              {loginError && <p className="text-red-600 text-center text-sm" data-testid="admin-login-error">{loginError}</p>}
              <button type="submit" className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors" data-testid="admin-login-btn">Logg inn</button>
            </form>
            <button onClick={() => navigate('/')} className="w-full mt-4 text-stone-600 hover:text-stone-900 flex items-center justify-center gap-2">
              <ArrowLeft size={20} /> Tilbake til forsiden
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Oversikt' },
    { id: 'dealers', label: 'Forhandlere' },
    { id: 'demo-promote', label: 'Demo-forhandlere' },
    { id: 'kits', label: 'Kit-bestillinger' },
    { id: 'diy-orders', label: 'DIY-bestillinger' },
    { id: 'marketing', label: 'Markedsføring' },
    { id: 'contact-requests', label: 'Kontaktskjema' },
    { id: 'warranties', label: 'Garantier' },
    { id: 'expiring', label: 'Utlopende' },
    { id: 'leads', label: 'Oppfolging' },
    { id: 'gallery', label: 'Galleri' },
    { id: 'blog', label: 'Blogg' },
    { id: 'new-dealer', label: '+ Ny forhandler' },
    { id: 'new-kit', label: '+ Nytt kit' },
    { id: 'datasheets', label: 'Datablader' },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <h1 className="text-xl sm:text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Admin</h1>
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-emerald-100 hover:text-white text-xs sm:text-sm"><ArrowLeft size={14} /> <span className="hidden sm:inline">Forsiden</span></button>
            <button onClick={() => { setIsAuthenticated(false); setCredentials({ username: '', password: '' }); }} className="flex items-center gap-1.5 text-emerald-100 hover:text-white text-xs sm:text-sm" data-testid="admin-logout-btn"><LogOut size={14} /> Logg ut</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-stone-200 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 flex space-x-0.5 sm:space-x-1">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
              className={`py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-emerald-800 text-emerald-800' : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'dashboard' && <DashboardTab stats={stats} auth={getAuth()} />}
        {activeTab === 'dealers' && <DealersTab dealers={dealers} loading={loading} auth={getAuth()} loadDealers={loadDealers} />}
        {activeTab === 'demo-promote' && <DemoPromoteTab auth={getAuth()} dealers={dealers} loadDealers={loadDealers} />}
        {activeTab === 'kits' && <KitOrdersTab kitOrders={kitOrders} loading={loading} auth={getAuth()} dealers={dealers} loadDealers={loadDealers} loadKitOrders={loadKitOrders} />}
        {activeTab === 'diy-orders' && <DIYOrdersTab auth={getAuth()} />}
        {activeTab === 'marketing' && <MarketingTab auth={getAuth()} />}
        {activeTab === 'contact-requests' && <ContactRequestsTab auth={getAuth()} />}
        {activeTab === 'warranties' && <WarrantiesTab warranties={warranties} loading={loading} />}
        {activeTab === 'expiring' && <ExpiringWarrantiesTab auth={getAuth()} />}
        {activeTab === 'leads' && <LeadsOverviewTab auth={getAuth()} />}
        {activeTab === 'gallery' && <GalleryTab auth={getAuth()} />}
        {activeTab === 'blog' && <BlogTab auth={getAuth()} />}
        {activeTab === 'new-dealer' && <NewDealerTab auth={getAuth()} onSuccess={() => { setActiveTab('dealers'); loadDealers(); }} />}
        {activeTab === 'new-kit' && <NewKitTab auth={getAuth()} dealers={dealers} onSuccess={() => { setActiveTab('kits'); loadKitOrders(); }} loadDealers={loadDealers} />}
        {activeTab === 'datasheets' && <DatasheetsTab auth={getAuth()} />}
      </div>
    </div>
  );
}

function DashboardTab({ stats, auth }) {
  const [sending, setSending] = useState(false);
  const [reportMsg, setReportMsg] = useState('');
  const [diyStats, setDiyStats] = useState(null);

  const sendReport = async () => {
    setSending(true);
    setReportMsg('');
    try {
      const res = await fetch(`${API}/admin/weekly-report`, { method: 'POST', headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setReportMsg('Rapport sendt til robert@gravfix.no!');
      else setReportMsg('Kunne ikke sende rapport');
    } catch { setReportMsg('Tilkobling feilet'); }
    setSending(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/diy-stats`, { headers: { 'Authorization': `Basic ${auth}` } });
        if (res.ok) setDiyStats(await res.json());
      } catch { /* ignore */ }
    })();
  }, [auth]);

  if (!stats) return <div className="text-stone-500">Laster...</div>;
  const cards = [
    { icon: <Users className="text-emerald-800" size={28} />, title: 'Aktive forhandlere', value: stats.total_dealers, bg: 'bg-emerald-50' },
    { icon: <Shield className="text-amber-700" size={28} />, title: 'Totale garantier', value: stats.total_warranties, bg: 'bg-amber-50' },
    { icon: <Package className="text-blue-700" size={28} />, title: 'Test kits', value: stats.test_kits, bg: 'bg-blue-50' },
    { icon: <Package className="text-purple-700" size={28} />, title: 'Forhandler kits', value: stats.purchase_kits, bg: 'bg-purple-50' },
    { icon: <Package className="text-green-700" size={28} />, title: 'DIY kits solgt', value: stats.diy_kits_sold, bg: 'bg-green-50' },
    { icon: <Shield className="text-stone-700" size={28} />, title: 'Standard garantier', value: stats.standard_warranties, bg: 'bg-stone-100' },
    { icon: <Shield className="text-teal-700" size={28} />, title: 'DIY garantier', value: stats.diy_warranties, bg: 'bg-teal-50' },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-stone-900">Statistikk</h2>
        <div className="flex items-center gap-3">
          {reportMsg && <span className="text-sm text-emerald-600">{reportMsg}</span>}
          <button onClick={sendReport} disabled={sending} className="flex items-center gap-2 bg-stone-900 text-white hover:bg-stone-800 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50" data-testid="send-report-btn">
            <Mail size={16} /> {sending ? 'Sender...' : 'Send ukerapport'}
          </button>
        </div>
      </div>

      {diyStats && <DIYStatsPanel diy={diyStats} />}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className={`${c.bg} rounded-xl p-5 border border-stone-200`} data-testid={`stat-card-${i}`}>
            <div className="mb-3">{c.icon}</div>
            <p className="text-3xl font-bold text-stone-900">{c.value}</p>
            <p className="text-sm text-stone-600 mt-1">{c.title}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DIYStatsPanel({ diy }) {
  const fmtKr = (n) => `${(n || 0).toLocaleString('nb-NO')} kr`;
  return (
    <div className="mb-6 space-y-3" data-testid="diy-stats-panel">
      {diy.low_stock && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 flex items-start gap-3" data-testid="diy-low-stock-banner">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={22} />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900">Lavt DIY-lager: kun {diy.free_warranty_pool} ledige garantinumre</h4>
            <p className="text-sm text-red-800 mt-0.5">
              Du har færre enn {diy.low_stock_threshold} ledige DIY-garantinumre igjen. Generer en ny batch i fanen <strong>+ Nytt kit</strong> (type "diy") for å unngå at nye bestillinger blir stående uten kit.
            </p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white rounded-xl p-5 sm:p-6 shadow-lg">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2"><Package size={20} className="text-amber-300" /> DIY-nettbutikk</h3>
            <p className="text-emerald-100 text-xs sm:text-sm mt-0.5">Salgsoversikt fra <code className="font-mono">/bestill-kit</code></p>
          </div>
          {diy.pending_orders > 0 && (
            <span className="bg-amber-400 text-stone-900 text-xs font-semibold px-3 py-1 rounded-full" data-testid="diy-pending-badge">
              {diy.pending_orders} ny{diy.pending_orders === 1 ? '' : 'e'} bestilling{diy.pending_orders === 1 ? '' : 'er'}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white/10 backdrop-blur rounded-lg p-3 sm:p-4" data-testid="diy-stat-month-kits">
            <p className="text-xs text-emerald-100 uppercase tracking-wide">Solgt denne måneden</p>
            <p className="text-2xl sm:text-3xl font-bold mt-1">{diy.kits_sold_month}</p>
            <p className="text-xs text-emerald-200">kit</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-3 sm:p-4" data-testid="diy-stat-month-revenue">
            <p className="text-xs text-emerald-100 uppercase tracking-wide">Omsetning denne måneden</p>
            <p className="text-2xl sm:text-3xl font-bold mt-1 text-amber-300">{fmtKr(diy.revenue_month)}</p>
            <p className="text-xs text-emerald-200">inkl. mva</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-3 sm:p-4" data-testid="diy-stat-total-kits">
            <p className="text-xs text-emerald-100 uppercase tracking-wide">Totalt solgt</p>
            <p className="text-2xl sm:text-3xl font-bold mt-1">{diy.kits_sold_total}</p>
            <p className="text-xs text-emerald-200">{fmtKr(diy.revenue_total)} omsetning</p>
          </div>
          <div className={`${diy.low_stock ? 'bg-red-500/30 border border-red-300/40' : 'bg-white/10'} backdrop-blur rounded-lg p-3 sm:p-4`} data-testid="diy-stat-pool">
            <p className="text-xs text-emerald-100 uppercase tracking-wide">Ledige garantinumre</p>
            <p className={`text-2xl sm:text-3xl font-bold mt-1 ${diy.low_stock ? 'text-red-200' : ''}`}>{diy.free_warranty_pool}</p>
            <p className="text-xs text-emerald-200">av {diy.total_diy_pool} totalt</p>
          </div>
        </div>
      </div>
    </div>
  );
}



function WarrantiesTab({ warranties, loading }) {
  if (loading) return <div className="text-stone-500">Laster...</div>;
  const active = warranties.filter(w => w.status === 'active');
  const reserved = warranties.filter(w => w.status === 'reserved');

  return (
    <div>
      <h2 className="text-2xl font-semibold text-stone-900 mb-6">
        Garantier ({active.length} aktive{reserved.length > 0 ? `, ${reserved.length} reserverte` : ''})
      </h2>
      {active.length === 0 ? (
        <p className="text-stone-500">Ingen aktive garantier.</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-stone-200" data-testid="warranties-table">
            <thead className="bg-stone-50">
              <tr>
                {['Garantinr', 'Kunde', 'E-post', 'Telefon', 'Forhandler', 'Registrert', 'Utloper'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {active.map((w) => (
                <tr key={w.id} className="hover:bg-stone-50">
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-800">{w.warranty_number}</td>
                  <td className="px-4 py-3 text-sm font-medium text-stone-900">{w.customer_name}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{w.email}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{w.phone}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{w.dealer_name || 'DIY / Privat'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{new Date(w.registration_date).toLocaleDateString('no-NO')}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{new Date(w.warranty_end_date).toLocaleDateString('no-NO')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ExpiringWarrantiesTab({ auth }) {
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkMessage, setCheckMessage] = useState('');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/admin/expiring-warranties`, { headers: { 'Authorization': `Basic ${auth}` } });
        if (res.ok) setExpiring(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [auth]);

  const triggerCheck = async () => {
    setChecking(true);
    setCheckMessage('');
    try {
      const res = await fetch(`${API}/admin/check-expiring-warranties`, { method: 'POST', headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) {
        const data = await res.json();
        setCheckMessage(data.message);
      } else {
        const err = await res.json().catch(() => ({}));
        setCheckMessage(err.detail || 'Kunne ikke sjekke garantier.');
      }
    } catch { setCheckMessage('Kunne ikke koble til serveren.'); }
    setChecking(false);
  };

  const getDaysUntilExpiry = (endDate) => {
    const diff = new Date(endDate) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) return <div className="text-stone-500">Laster...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-stone-900">Utløpende garantier (neste 6 mnd)</h2>
        <button onClick={triggerCheck} disabled={checking} className="flex items-center gap-2 bg-amber-600 text-white hover:bg-amber-700 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50" data-testid="trigger-expiry-check-btn">
          <Bell size={16} /> {checking ? 'Sjekker...' : 'Send varsel nå'}
        </button>
      </div>
      {checkMessage && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm" data-testid="expiry-check-message">{checkMessage}</div>}

      {expiring.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-12 text-center">
          <Shield className="mx-auto text-stone-300 mb-4" size={48} />
          <p className="text-stone-500">Ingen garantier utløper de neste 6 månedene.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="expiring-warranties-list">
          {expiring.map((w) => {
            const days = getDaysUntilExpiry(w.warranty_end_date);
            return (
              <div key={w.warranty_number || w.id} className="bg-white rounded-xl shadow-sm border border-stone-200 p-5 flex items-start justify-between gap-4 hover:shadow-md transition-shadow">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-1 rounded-full">
                      <AlertTriangle size={12} /> {days} dager igjen
                    </span>
                    <span className="text-xs text-stone-400">{w.warranty_number}</span>
                  </div>
                  <h3 className="font-semibold text-stone-900">{w.customer_name}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-stone-600">
                    <span>Tlf: {w.phone}</span>
                    <span>{w.email}</span>
                    <span>{w.address}</span>
                  </div>
                  {w.dealer_name && <p className="text-xs text-stone-400 mt-1">Forhandler: {w.dealer_name}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-stone-400">Utloper</p>
                  <p className="text-sm font-bold text-red-600">{new Date(w.warranty_end_date).toLocaleDateString('no-NO')}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-6 p-4 bg-stone-100 rounded-lg text-sm text-stone-600">
        <p><strong>Automatisk varsel:</strong> Du mottar en e-post til robert@gravfix.no 3 måneder for garantien utloper.</p>
      </div>
    </div>
  );
}

function LeadsOverviewTab({ auth }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/admin/leads`, { headers: { 'Authorization': `Basic ${auth}` } });
        if (res.ok) setLeads(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [auth]);

  if (loading) return <div className="text-stone-500">Laster...</div>;

  // Group by dealer
  const byDealer = {};
  leads.forEach(l => {
    const name = l.dealer_name || 'Ukjent';
    if (!byDealer[name]) byDealer[name] = [];
    byDealer[name].push(l);
  });

  const totalConverted = leads.filter(l => l.status === 'converted').length;
  const totalContacted = leads.filter(l => l.status === 'contacted').length;
  const totalPending = leads.filter(l => l.status === 'pending').length;

  return (
    <div>
      <h2 className="text-2xl font-semibold text-stone-900 mb-6">Oppfolging - kunder uten DC-Protect</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-stone-50 rounded-xl p-4 border border-stone-200 text-center">
          <p className="text-3xl font-bold text-stone-900">{leads.length}</p>
          <p className="text-sm text-stone-500">Totalt registrert</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
          <p className="text-3xl font-bold text-amber-800">{totalPending}</p>
          <p className="text-sm text-stone-500">Venter</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-center">
          <p className="text-3xl font-bold text-blue-800">{totalContacted}</p>
          <p className="text-sm text-stone-500">Kontaktet</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
          <p className="text-3xl font-bold text-emerald-800">{totalConverted}</p>
          <p className="text-sm text-stone-500">Konvertert til DC-Protect</p>
        </div>
      </div>

      {leads.length === 0 ? (
        <p className="text-stone-500">Ingen oppfolginger registrert ennå.</p>
      ) : (
        <div className="space-y-8" data-testid="admin-leads-overview">
          {Object.entries(byDealer).map(([dealerName, dealerLeads]) => {
            const conv = dealerLeads.filter(l => l.status === 'converted').length;
            const rate = dealerLeads.length > 0 ? Math.round((conv / dealerLeads.length) * 100) : 0;
            return (
              <div key={dealerName}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-stone-900">{dealerName}</h3>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-stone-500">{dealerLeads.length} kunder</span>
                    <span className="text-emerald-700 font-semibold">{conv} konvertert ({rate}%)</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-x-auto">
                  <table className="min-w-full divide-y divide-stone-200">
                    <thead className="bg-stone-50">
                      <tr>
                        {['Kunde', 'Telefon', 'E-post', 'Adresse', 'Oppfrisket', 'Oppfolging', 'Status'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {dealerLeads.map(l => {
                        const statusColors = { pending: 'bg-stone-100 text-stone-700', contacted: 'bg-amber-100 text-amber-800', converted: 'bg-emerald-100 text-emerald-800', declined: 'bg-red-100 text-red-700' };
                        const statusLabels = { pending: 'Venter', contacted: 'Kontaktet', converted: 'Konvertert', declined: 'Avslått' };
                        return (
                          <tr key={l.id} className="hover:bg-stone-50">
                            <td className="px-4 py-3 text-sm font-medium text-stone-900">{l.customer_name}</td>
                            <td className="px-4 py-3 text-sm text-stone-600">{l.phone}</td>
                            <td className="px-4 py-3 text-sm text-stone-600">{l.email || '-'}</td>
                            <td className="px-4 py-3 text-sm text-stone-600">{l.address}</td>
                            <td className="px-4 py-3 text-sm text-stone-600">{l.service_date?.slice(0, 10)}</td>
                            <td className="px-4 py-3 text-sm text-stone-600">{l.follow_up_date?.slice(0, 10)}</td>
                            <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[l.status]}`}>{statusLabels[l.status]}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function NewDealerTab({ auth, onSuccess }) {
  const [formData, setFormData] = useState({
    company_name: '', contact_person: '', email: '', phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);

  const loadInvitations = async () => {
    try {
      const res = await fetch(`${API}/admin/dealers/invitations`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setInvitations(await res.json());
    } catch {}
    finally { setLoadingInv(false); }
  };

  useEffect(() => { loadInvitations(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/admin/dealers/invite`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, site_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        setMessage(data.message || 'Invitasjon sendt!');
        setFormData({ company_name: '', contact_person: '', email: '', phone: '' });
        loadInvitations();
      } else {
        const err = await res.json();
        setMessage(err.detail || 'Kunne ikke sende invitasjon.');
      }
    } catch { setMessage('Kunne ikke koble til serveren.'); }
    finally { setIsSubmitting(false); }
  };

  const [resendingId, setResendingId] = useState(null);
  const [resendMsg, setResendMsg] = useState('');

  const handleResend = async (id) => {
    setResendingId(id);
    setResendMsg('');
    try {
      const res = await fetch(`${API}/admin/dealers/invitations/${id}/resend`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_url: window.location.origin }),
      });
      if (res.ok) {
        setResendMsg('Ny invitasjon sendt!');
        loadInvitations();
      } else {
        const err = await res.json().catch(() => ({}));
        setResendMsg(err.detail || 'Kunne ikke sende invitasjon.');
      }
    } catch {
      setResendMsg('Kunne ikke koble til serveren.');
    }
    finally { setResendingId(null); setTimeout(() => setResendMsg(''), 5000); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sikker på at du vil slette denne invitasjonen?')) return;
    try {
      const res = await fetch(`${API}/admin/dealers/invitations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });
      if (res.ok) loadInvitations();
    } catch {}
  };

  const getTimeLeft = (expiresAt) => {
    const now = new Date();
    const exp = new Date(expiresAt);
    const diff = exp - now;
    if (diff <= 0) return { text: 'Utlopt', expired: true };
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return { text: `${hours}t ${minutes}m igjen`, expired: false };
    return { text: `${minutes}m igjen`, expired: false };
  };

  const fields = [
    { key: 'company_name', label: 'Firmanavn', required: true },
    { key: 'contact_person', label: 'Kontaktperson', required: true },
    { key: 'email', label: 'E-post', type: 'email', required: true },
    { key: 'phone', label: 'Telefon', type: 'tel', required: true },
  ];

  const pending = invitations.filter(i => i.status === 'pending');
  const completed = invitations.filter(i => i.status === 'completed');

  return (
    <div className="space-y-8">
      {/* Invite form */}
      <div className="max-w-2xl">
        <h2 className="text-2xl font-semibold text-stone-900 mb-2">Inviter ny forhandler</h2>
        <p className="text-stone-500 mb-6">Fyll inn forhandlerens kontaktinfo. En e-post med registreringslenke sendes automatisk, der forhandleren selv velger passord og PIN-kode.</p>
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 space-y-6" data-testid="new-dealer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-stone-700 mb-2">{f.label} {f.required && '*'}</label>
                <input type={f.type || 'text'} required={f.required} value={formData[f.key]} onChange={(e) => setFormData({...formData, [f.key]: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" />
              </div>
            ))}
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
            <p className="text-sm text-emerald-800">
              <strong>Slik fungerer det:</strong> Forhandleren mottar en e-post med en registreringslenke (gyldig i 24 timer). Der fyller de inn postadresse, passord og PIN-kode selv.
            </p>
          </div>
          <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-50" data-testid="invite-dealer-btn">
            {isSubmitting ? 'Sender invitasjon...' : 'Send invitasjon'}
          </button>
          {message && <p className={`text-center ${message.includes('sendt') ? 'text-emerald-600' : 'text-red-600'}`}>{message}</p>}
        </form>
      </div>

      {/* Pending invitations */}
      {pending.length > 0 && (
        <div data-testid="pending-invitations">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Ventende invitasjoner ({pending.length})</h3>
          {resendMsg && <p className={`text-sm mb-3 px-3 py-2 rounded-lg ${resendMsg.includes('sendt') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{resendMsg}</p>}
          <div className="space-y-3">
            {pending.map(inv => {
              const timeLeft = getTimeLeft(inv.expires_at);
              return (
                <div key={inv.id} className="bg-white rounded-xl border border-stone-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`invitation-${inv.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900">{inv.company_name}</p>
                    <p className="text-sm text-stone-500">{inv.contact_person} &middot; {inv.email}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${timeLeft.expired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`time-left-${inv.id}`}>
                      {timeLeft.expired ? 'Utlopt' : timeLeft.text}
                    </span>
                    <button onClick={() => handleResend(inv.id)} disabled={resendingId === inv.id} className="text-xs bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-lg hover:bg-emerald-200 font-medium transition-colors disabled:opacity-50" data-testid={`resend-${inv.id}`}>
                      {resendingId === inv.id ? 'Sender...' : timeLeft.expired ? 'Send på nytt' : 'Send påminnelse'}
                    </button>
                    <button onClick={() => handleDelete(inv.id)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1.5 transition-colors" data-testid={`delete-inv-${inv.id}`}>
                      Slett
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed invitations */}
      {completed.length > 0 && (
        <div data-testid="completed-invitations">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Fullforte registreringer ({completed.length})</h3>
          <div className="space-y-2">
            {completed.map(inv => (
              <div key={inv.id} className="bg-stone-50 rounded-xl border border-stone-200 p-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`completed-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-700">{inv.company_name}</p>
                  <p className="text-sm text-stone-400">{inv.contact_person} &middot; {inv.email}</p>
                </div>
                <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full flex-shrink-0">Registrert</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DatasheetsTab({ auth }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [productName, setProductName] = useState('');
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');

  const loadSheets = async () => {
    try {
      const res = await fetch(`${API}/admin/datasheets`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setSheets(await res.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadSheets(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !productName.trim()) return;
    setUploading(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('product_name', productName);
      const res = await fetch(`${API}/admin/datasheets`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}` },
        body: fd,
      });
      if (res.ok) {
        setMessage('Datablad lastet opp!');
        setProductName('');
        setFile(null);
        loadSheets();
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage(err.detail || 'Opplasting feilet.');
      }
    } catch { setMessage('Kunne ikke koble til serveren.'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Slette datablad "${name}"?`)) return;
    try {
      await fetch(`${API}/admin/datasheets/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });
      loadSheets();
    } catch {}
  };

  return (
    <div className="space-y-8">
      <div className="max-w-xl">
        <h2 className="text-2xl font-semibold text-stone-900 mb-2">Datablader</h2>
        <p className="text-stone-500 mb-6">Last opp produktdatablader (PDF) som forhandlere kan laste ned fra sin portal.</p>
        <form onSubmit={handleUpload} className="bg-white rounded-xl shadow-sm border border-stone-200 p-6 space-y-4" data-testid="upload-datasheet-form">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Produktnavn *</label>
            <input type="text" required value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="F.eks. DC-Protect eller Isopropanol" className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="product-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">PDF-fil *</label>
            <input type="file" accept=".pdf" required onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-800 file:font-medium hover:file:bg-emerald-100" data-testid="pdf-file-input" />
          </div>
          <button type="submit" disabled={uploading} className="bg-emerald-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-emerald-900 transition-colors disabled:opacity-50" data-testid="upload-btn">
            {uploading ? 'Laster opp...' : 'Last opp datablad'}
          </button>
          {message && <p className={`text-sm ${message.includes('opp!') ? 'text-emerald-600' : 'text-red-600'}`}>{message}</p>}
        </form>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Opplastede datablader ({sheets.length})</h3>
        {loading ? <p className="text-stone-400">Laster...</p> : sheets.length === 0 ? (
          <p className="text-stone-400">Ingen datablader lastet opp ennå.</p>
        ) : (
          <div className="space-y-3">
            {sheets.map(s => (
              <div key={s.id} className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-4" data-testid={`sheet-${s.id}`}>
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-red-600">PDF</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-900">{s.product_name}</p>
                  <p className="text-xs text-stone-400">{s.filename}</p>
                </div>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs bg-stone-100 text-stone-700 px-3 py-1.5 rounded-lg hover:bg-stone-200 font-medium">Åpne</a>
                <button onClick={() => handleDelete(s.id, s.product_name)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1.5">Slett</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function NewKitTab({ auth, dealers, onSuccess, loadDealers }) {
  const [formData, setFormData] = useState({ dealer_id: '', kit_type: 'standard', custom_qty: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => { if (dealers.length === 0) loadDealers(); }, [dealers.length, loadDealers]);

  const kitTypeInfo = {
    standard: { label: 'Standard eske (10 kit)', qty: 10 },
    'prøve': { label: 'Prøve-kit (2 kit)', qty: 2 },
    diy: { label: 'DIY kit (1 stk)', qty: 1 },
    egendefinert: { label: 'Egendefinert antall', qty: null },
  };

  const getQty = () => {
    if (formData.kit_type === 'egendefinert') return parseInt(formData.custom_qty) || 0;
    return kitTypeInfo[formData.kit_type]?.qty || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = getQty();
    if (qty < 1) { setMessage('Antall kit må være minst 1'); return; }
    setIsSubmitting(true);
    setMessage('');
    try {
      const kitType = formData.kit_type === 'egendefinert' ? 'standard' : formData.kit_type;
      const res = await fetch(`${API}/admin/kit-orders`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kit_type: kitType,
          kit_quantity: qty,
          dealer_id: formData.dealer_id || null,
        })
      });
      if (res.ok) {
        const data = await res.json();
        setMessage(`Eske opprettet! Eskenr: ${data.box_number} | ${data.warranty_numbers.length} garantinumre: ${data.warranty_numbers[0]} - ${data.warranty_numbers[data.warranty_numbers.length - 1]}`);
        setTimeout(() => onSuccess(), 3000);
      } else {
        const err = await res.json().catch(() => ({ detail: `Serverfeil (${res.status})` }));
        setMessage(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail || 'Kunne ikke opprette eske.'));
      }
    } catch (e) { setMessage('Nettverksfeil: ' + e.message); }
    finally { setIsSubmitting(false); }
  };

  const qty = getQty();

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-semibold text-stone-900 mb-6">Opprett ny eske</h2>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 space-y-6" data-testid="new-kit-form">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Eske-type *</label>
          <select required value={formData.kit_type} onChange={(e) => setFormData({...formData, kit_type: e.target.value, custom_qty: ''})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent">
            <option value="standard">Standard eske (10 kit)</option>
            <option value="prøve">Prøve-kit for nye forhandlere (2 kit)</option>
            <option value="diy">DIY kit</option>
            <option value="egendefinert">Egendefinert antall</option>
          </select>
        </div>

        {formData.kit_type === 'egendefinert' && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Antall kit *</label>
            <input
              type="number"
              min="1"
              max="100"
              required
              value={formData.custom_qty}
              onChange={(e) => setFormData({...formData, custom_qty: e.target.value})}
              placeholder="Skriv inn antall kit..."
              className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
              data-testid="custom-qty-input"
            />
          </div>
        )}
        
        <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
          <p className="text-sm text-stone-600">
            <strong>Antall kit:</strong> {qty > 0 ? `${qty} stk` : 'Velg antall'}<br/>
            <strong>Eskenummer:</strong> Genereres automatisk<br/>
            <strong>Garantinumre:</strong> {qty > 0 ? `${qty} stk genereres automatisk (GDC-${new Date().getFullYear()}-XXXX)` : '-'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Forhandler (valgfritt - kan tilordnes senere)</label>
          <select value={formData.dealer_id} onChange={(e) => setFormData({...formData, dealer_id: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent">
            <option value="">Ingen forhandler (forhåndspakking)</option>
            {dealers.map((d) => <option key={d.id} value={d.id}>{d.company_name} - {d.contact_person}</option>)}
          </select>
          <p className="text-xs text-stone-400 mt-1">Du kan tilordne en forhandler senere når esken sendes</p>
        </div>

        <button type="submit" disabled={isSubmitting || qty < 1} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-50" data-testid="create-kit-btn">
          {isSubmitting ? 'Oppretter...' : `Opprett eske med ${qty > 0 ? qty : '?'} kit`}
        </button>
        {message && <p className={`text-center text-sm mt-2 ${message.includes('opprettet') ? 'text-emerald-600' : 'text-red-600'}`}>{message}</p>}
      </form>
    </div>
  );
}

// =====================  DIY Kit Orders (customer-facing shop)  =====================


export default AdminDashboard;
